import { gql } from '@apollo/client'
import { JOB_FRAGMENT, WORKER_FRAGMENT, WORKED_TIMESHEET_FRAGMENT } from '../apollo.fragment'

export const GET_CURRENT_WORKER_ASSIGNMENT_QUERY = gql`
	query {
		currentWorkerAssignment {
			id
		}
	}
`

export const GET_WORKER_CURRENT_JOB_QUERY = gql`
	query getWorkerCurrentJobQuery($workerId: String!, $filterScope: FilterAssignmentScopeEnum, $after: String, $before: String, $first: Int, $last: Int) {
		recruiterWorkerAssignments(workerId: $workerId, filterScope: $filterScope, after: $after, before: $before, first: $first, last: $last) {
			nodes {
				id
				status
				createdAt
				updatedAt
				startDate
				endDate
				job {
					...jobFragment
				}
				worker {
					...workerFragment
				}
				workedTimesheets {
					...workedTimesheetFragment
				}
			}
		}
	}

	${JOB_FRAGMENT}
	${WORKER_FRAGMENT}
	${WORKED_TIMESHEET_FRAGMENT}
`

export const GET_WORKER_PAST_JOB_QUERY = gql`
	query getWorkerPastJobQuery($workerId: String!, $filterScope: FilterAssignmentScopeEnum, $after: String, $before: String, $first: Int, $last: Int) {
		recruiterWorkerAssignments(workerId: $workerId, filterScope: $filterScope, after: $after, before: $before, first: $first, last: $last) {
			nodes {
				id
				status
				createdAt
				updatedAt
				startDate
				endDate
				job {
					...jobFragment
				}
				worker {
					...workerFragment
				}
			}
		}
	}

	${JOB_FRAGMENT}
	${WORKER_FRAGMENT}
`

export const GET_JOB_DETAIL_QUERY = gql`
	query getJobDetailQuery($id: String!) {
		workerAssignment(id: $id) {
			id
			status
			createdAt
			updatedAt
			startDate
			endDate
			job {
				...jobFragment
			}
			worker {
				...workerFragment
			}
			workedTimesheets {
				...workedTimesheetFragment
			}
		}
	}

	${JOB_FRAGMENT}
	${WORKER_FRAGMENT}
	${WORKED_TIMESHEET_FRAGMENT}
`

export const LIKE_DISLIKE_JOB_MUTATION = gql`
	mutation LikeOrDislikeAJob($jobId: String!, $action: LikeActionEnum!) {
		likeOrDislikeAJob(jobId: $jobId, action: $action) {
			...jobFragment
		}
	}
	${JOB_FRAGMENT}
`

/*
 * HS-487 Home › Recent Applications / Maestro V2 Jobs › Submissions: every
 * application the recruiter can see, across all jobs. `appliedDatetime` carries
 * the time of day that `appliedDate` (a Date) drops; it needs maestro-api with
 * that field deployed first.
 *
 * Tracking is a second document, not `@include`: a backend whose gem predates
 * `applicationTracking` rejects the whole query when the field is merely named.
 */
const RECENT_JOB_APPLICATIONS_NODE_FIELDS = `
	id
	status
	stage
	atsStatus
	atsSource
	appliedDate
	appliedDatetime
	startDatetime
	worker {
		id
		fullName
		firstName
		lastName
		email
		phone
		user {
			id
		}
		employer {
			id
			fullName
			firstName
			lastName
			email
		}
	}
	job {
		id
		externalFriendlyId
		atsSource
		jobGeneral {
			title
		}
		facility {
			name
		}
	}
`

const recentJobApplicationsQuery = (operationName, extraNodeFields = '', withSummary = false) => gql`
	query ${operationName}(
		$filter: MaestroRecruiterJobApplicantFilter
		$sortBy: MaestroRecruiterSort
		$after: String
		$first: Int
		${withSummary ? ', $summaryRequire: Boolean' : ''}
	) {
		searchJobApplicants(
			filter: $filter
			sortBy: $sortBy
			after: $after
			first: $first
			${withSummary ? ', summaryRequire: $summaryRequire' : ''}
		) {
			nodes {
				${RECENT_JOB_APPLICATIONS_NODE_FIELDS}
				${extraNodeFields}
			}
			totalCount
			${withSummary ? 'summaryResults' : ''}
			pageInfo {
				hasNextPage
				endCursor
			}
		}
	}
`

export const RECENT_JOB_APPLICATIONS_QUERY = recentJobApplicationsQuery('recentJobApplicationsQuery')

export const APPLICATION_TRACKING_FIELDS = `
	applicationTracking {
		refSource
		refId
		refCampaign
		originalSource
		utmSource
		utmMedium
		utmCampaign
		utmContent
		utmTerm
	}
`

export const RECENT_JOB_APPLICATIONS_TRACKING_QUERY = recentJobApplicationsQuery('recentJobApplicationsTrackingQuery', APPLICATION_TRACKING_FIELDS, true)

/*
 * Applicant sources (SPT-2326): every application the recruiter can see, with its tracking values and the
 * facets (`creators`, `utmSources`, …) the page's filters and breakdowns are built from. Only sent on a
 * tenant with `maestro_applicant_source_tracking`.
 */
export const APPLICANT_SOURCES_QUERY = recentJobApplicationsQuery('applicantSourcesQuery', APPLICATION_TRACKING_FIELDS, true)

const JOB_APPLICANTS_POPUP_NODE_FIELDS = `
	id
	appliedDate
	appliedDatetime
	stage
	status
	atsStatus
	atsSource
	source
	worker {
		id
		fullName
		firstName
		lastName
		email
		atsStatus
	}
`

const jobApplicantsPopupQuery = (operationName, extraNodeFields = '') => gql`
	query ${operationName}(
		$filter: MaestroRecruiterJobApplicantFilter
		$sortBy: MaestroRecruiterSort
		$first: Int
		$after: String
		$summaryRequire: Boolean
	) {
		searchJobApplicants(filter: $filter, sortBy: $sortBy, first: $first, after: $after, summaryRequire: $summaryRequire) {
			nodes {
				${JOB_APPLICANTS_POPUP_NODE_FIELDS}
				${extraNodeFields}
			}
			totalCount
			summaryResults
		}
	}
`

/*
 * Jobs › Submission › View Applicants popup (HS-486). Its own query so the Job info › Submissions tab,
 * which shares the older one, does not depend on `source` being served.
 */
export const JOB_APPLICANTS_POPUP_QUERY = jobApplicantsPopupQuery('jobApplicantsPopupQuery')

/*
 * The same popup on a tenant with `maestro_applicant_source_tracking` (SPT-2326). A separate document,
 * not `@include`: a backend whose gem predates `applicationTracking` rejects the whole query when the
 * field is merely named, so a tenant without the flag must never send it.
 */
export const JOB_APPLICANTS_POPUP_TRACKING_QUERY = jobApplicantsPopupQuery('jobApplicantsPopupTrackingQuery', APPLICATION_TRACKING_FIELDS)
